import { useState, useEffect } from 'react';
import { useSubscription } from '@apollo/client';
import { Registration } from '../API';
import { ON_CREATE_REGISTRATION } from '../graphql/subscriptions/onCreateRegistration';
import { ON_UPDATE_REGISTRATION } from '../graphql/subscriptions/onUpdateRegistration';
import { Attendee } from '../components/ZplPrinter';
import { individualAttendanceEnabled } from '../constants/attendance';
import { isIndividual } from '../utils/partyAttendance';
import { usePartyRefresh } from './usePartyRefresh';

export function useRegistrationSubscriptions(
  eventId: string | undefined,
  initialRegistrations: Registration[],
  printingEnabled: boolean,
  onPrintTrigger: (attendee: Attendee) => void,
) {
  const [registrations, setRegistrations] = useState<Registration[]>(initialRegistrations);
  useEffect(() => { setRegistrations(initialRegistrations); }, [eventId, initialRegistrations]);
  const { refreshParty, refreshAll, refreshError } = usePartyRefresh(eventId, registrations, setRegistrations);

  const { data: newRegistrationData, error: newRegistrationError } = useSubscription<{ onCreateRegistration: Registration }>(ON_CREATE_REGISTRATION, {
    variables: { eventId },
  });

  const { data: updatedRegistrationData, error: updatedRegistrationError } = useSubscription<{ onUpdateRegistration: Registration }>(ON_UPDATE_REGISTRATION, {
    variables: { eventId },
  });

  useEffect(() => {
    if (newRegistrationError) {
      console.error('Error with new registration subscription:', newRegistrationError);
    }
    if (newRegistrationData) {
      const newRegistration = newRegistrationData.onCreateRegistration;
      if (!newRegistration || newRegistration.eventId !== eventId) return;
      if (individualAttendanceEnabled) {
        void refreshParty(newRegistration.registrationId).catch(() => void refreshAll());
        return;
      }
      setRegistrations(prev => [
        ...prev.filter(r => r.registrationId !== newRegistration.registrationId),
        newRegistration,
      ]);
    }
  }, [newRegistrationData, newRegistrationError, eventId, refreshParty, refreshAll]);

  useEffect(() => {
    if (updatedRegistrationError) {
      console.error('Error with updated registration subscription:', updatedRegistrationError);
    }
    if (updatedRegistrationData) {
      const updatedRegistration = updatedRegistrationData.onUpdateRegistration;
      if (!updatedRegistration || updatedRegistration.eventId !== eventId) return;
      if (individualAttendanceEnabled) {
        void refreshParty(updatedRegistration.registrationId).then(party => {
          if (party && !isIndividual(party) && printingEnabled && party.registrationStatus === 'CHECKED_IN') onPrintTrigger(party);
        }).catch(() => void refreshAll());
        return;
      }
      setRegistrations(prev =>
        prev.map(reg => reg.registrationId === updatedRegistration.registrationId ? updatedRegistration : reg)
      );
      if (printingEnabled && updatedRegistration.registrationStatus === 'CHECKED_IN') {
        onPrintTrigger(updatedRegistration);
      }
    }
  }, [updatedRegistrationData, updatedRegistrationError, printingEnabled, onPrintTrigger, eventId, refreshParty, refreshAll]);

  return { registrations, setRegistrations, refreshAll, refreshError };
}
